import { ActionCreatorWithoutPayload, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IdentityCheckStatus, IdentityCheckStatuses, TransformedIdentityVerificationDocument } from '@afterpaytouch/portal-api/identity/types'
import {
  Country,
  CreditFacilityStatus,
  PaymentMethodType,
  SubscriptionStatus,
  IdentityStatus,
  ConsumerLookupRecognizedBy,
  ConsumerLookupCohort,
} from '@afterpaytouch/portal-api/types'
import {
  accountLookup,
  authReset,
  authResetChallenge,
  closeCheckout,
  confirmRegistration,
  confirmVerifyAccountMobile,
  emailLookup,
  mobileLookup,
  updateAccountDetails,
  authLogin,
  getMarketingPreferences,
  getIdentities,
  getEfaCode,
  getCheckout,
  identityNationalIDNumber,
  startCheckout,
  getFacilityCohortForNewUsers,
  getFacilityCohortForExistingUsers,
} from '../'
import {
  AccountStatus,
  ConsumerAddress,
  SupportedLocale,
  ProfileErrorCode,
  ProfileErrors,
  IdentityCheckMetadata,
  isPublicResponse,
  ConsumerFacilityCohort,
  AU_REG_ERROR_CODES,
} from '../../model'
import { maskPhoneNumber, phoneNumberIsCleared } from '../../utils/phone-numbers'
import { toVerifiedDocument, transformIdentityDocument } from '../../utils/array'
import { PURGE } from 'redux-persist'
import { identityVerify } from './hooks'
import { checkIdentities } from '../identityBiometric'
import { isNullOrUndefined } from '@afterpay/utils'
import { CashEligibilitySignals, CashLoginType } from '@afterpaytouch/portal-api/consumer/lookup'
import { isPortalApiError } from '@afterpaytouch/portal-api'

export interface ConsumerProfile {
  givenNames?: string
  otherGivenNames?: string
  surname?: string
  phoneNumber?: string
  contactAddress?: ConsumerAddress
  dateOfBirth?: string
  confirmed: boolean
}

export interface ConsumerState {
  accountCountry?: Country
  accountStatus?: AccountStatus
  creditCheckConsented?: boolean
  email?: string
  emailLookupCountry?: Country
  error?: ProfileErrorCode
  hasFeatureFlagsReady: boolean
  hasPassword: boolean
  id?: number
  identityCheckMetadata?: IdentityCheckMetadata
  identityCheckStatus?: IdentityCheckStatus
  identityCheckStatuses?: IdentityCheckStatuses
  mobile?: string
  preferredLocale?: SupportedLocale
  preferredPaymentMethod?: PaymentMethodType
  profile?: ConsumerProfile
  sessionExpiryUrl?: string
  smsVerificationEnabled: boolean
  uuid?: string
  verifiedIdentityDocuments?: TransformedIdentityVerificationDocument[]
  verifyAccountMobileRequired?: boolean
  isUserVerifyAuthenticatorAvailable: boolean
  // backend determines if trusted session by using fingerprint, device id and ip address
  // there is a distinction between isTrustedCheckout and this: this does not have to do with auth
  // just whether or not the user is on a recognised device
  trustedSession: boolean
  latestFacilityStatus?: CreditFacilityStatus
  marketingOptIn?: SubscriptionStatus
  isEmailExists: boolean
  isMobileExists: boolean
  cashtag?: string | null
  loginIdentityError?: string
  cashEligibilitySignals: CashEligibilitySignals | null
  cashCreationDate?: number
  cashLoginType?: CashLoginType
  shadowAccountExists?: boolean
  consumerFacilityCohort?: ConsumerFacilityCohort
}

export const consumerInitialState: ConsumerState = {
  error: undefined,
  hasFeatureFlagsReady: false,
  hasPassword: false,
  sessionExpiryUrl: undefined,
  smsVerificationEnabled: true,
  isUserVerifyAuthenticatorAvailable: false,
  trustedSession: false,
  creditCheckConsented: undefined,
  isEmailExists: false,
  isMobileExists: false,
  loginIdentityError: undefined,
  cashEligibilitySignals: null,
  cashLoginType: undefined,
  shadowAccountExists: undefined,
  consumerFacilityCohort: undefined,
}

const slice = createSlice({
  name: 'consumer',
  initialState: consumerInitialState,
  reducers: {
    clearSessionExpired(state: ConsumerState) {
      state.sessionExpiryUrl = undefined
    },
    updateConsumerMobile(state: ConsumerState, action: PayloadAction<string>): ConsumerState {
      return {
        ...state,
        mobile: action.payload,
      }
    },
    setLoginIdentityError(state: ConsumerState, action: PayloadAction<string>): ConsumerState {
      return {
        ...state,
        loginIdentityError: action.payload,
      }
    },
    updateConsumerAddress(state: ConsumerState, action: PayloadAction<ConsumerAddress>): ConsumerState {
      return {
        ...state,
        profile: {
          ...state.profile,
          confirmed: state.profile?.confirmed ?? false,
          contactAddress: action.payload,
        },
      }
    },
    setConsumerError(state: ConsumerState, action: PayloadAction<ProfileErrorCode | undefined>): ConsumerState {
      return {
        ...state,
        error: action.payload,
      }
    },
    setSessionExpired(state: ConsumerState, action: PayloadAction<string | undefined>): ConsumerState {
      return {
        ...state,
        sessionExpiryUrl: action.payload,
      }
    },
    clearConsumer(state: ConsumerState) {
      state.uuid = undefined
      state.profile = undefined
      state.preferredLocale = undefined
      state.preferredPaymentMethod = undefined
      state.accountStatus = undefined
      state.identityCheckStatus = undefined
      state.identityCheckStatuses = undefined
      state.hasPassword = false
      state.error = undefined
      state.cashtag = undefined
      state.cashEligibilitySignals = null
      state.cashCreationDate = undefined
      state.cashLoginType = undefined
      state.shadowAccountExists = undefined
    },
    setPreferredPaymentMethod(state: ConsumerState, action: PayloadAction<PaymentMethodType>) {
      state.preferredPaymentMethod = action.payload
    },
    setFeatureFlagState(state: ConsumerState, action: PayloadAction<boolean>) {
      state.hasFeatureFlagsReady = action.payload
    },
    setIsUserVerifyAuthenticatorAvailable(state, action) {
      state.isUserVerifyAuthenticatorAvailable = action.payload
    },
    setVerifyAccountMobileRequired(state, action) {
      state.verifyAccountMobileRequired = action.payload
    },
    setCashEligibilitySignal(state: ConsumerState, action: PayloadAction<CashEligibilitySignals | null | undefined>) {
      state.cashEligibilitySignals = action.payload ?? null
    },
    setCashCreationDate(state: ConsumerState, action: PayloadAction<number | undefined>) {
      state.cashCreationDate = action.payload
    },
    setCashLoginType(state: ConsumerState, action: PayloadAction<CashLoginType | undefined>) {
      state.cashLoginType = action.payload
    },
    setShadowAccountExists(state: ConsumerState, action: PayloadAction<boolean | undefined>) {
      state.shadowAccountExists = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateAccountDetails.fulfilled, (prevState, action) => {
        const { givenNames, otherGivenNames, surname, dateOfBirth, address1, address2, area2, suburb, state, postcode, countryCode } = action.meta.arg
        const updatedContactAddress = !isNullOrUndefined(prevState.profile?.contactAddress)
          ? {
              ...prevState.profile?.contactAddress,
              ...{ name: [givenNames, otherGivenNames, surname].filter(Boolean).join(' ') },
              address1,
              address2,
              area2,
              suburb,
              state,
              postcode,
              countryCode,
            }
          : undefined

        return {
          ...prevState,
          profile: {
            ...prevState.profile,
            givenNames,
            otherGivenNames,
            surname,
            dateOfBirth,
            confirmed: prevState.profile?.confirmed ?? false,
            contactAddress: updatedContactAddress,
          },
        }
      })
      .addCase(updateAccountDetails.pending, (state) => {
        state.error = undefined
      })
      .addCase(updateAccountDetails.rejected, (state, action) => {
        const errorCode = isPortalApiError(action?.payload) ? action.payload.errorCode : undefined
        state.error = !isNullOrUndefined(errorCode) ? ProfileErrors[errorCode] : ProfileErrorCode.Default
      })
      .addCase(accountLookup.fulfilled, (state, action) => {
        const account = action.payload
        state.id = account.id
        state.uuid = account.uuid
        state.email = account.email
        state.mobile = account.phoneNumber ?? ''
        state.profile = {
          givenNames: account.givenNames,
          otherGivenNames: account.otherGivenNames,
          surname: account.surname,
          phoneNumber: account.phoneNumber ?? '',
          contactAddress: account.contactAddress,
          dateOfBirth: account.dateOfBirth,
          confirmed: account.profileConfirmed,
        }
        state.accountCountry = account.countryCode
        state.preferredLocale = account.preferredLocale
        state.preferredPaymentMethod = account.preferredPaymentMethod
        state.accountStatus = account.accountStatus
        state.identityCheckStatus = account.identityCheckStatus
        state.identityCheckStatuses = account.identityCheckStatuses
        state.verifyAccountMobileRequired = phoneNumberIsCleared(account.phoneNumber ?? '')
        state.smsVerificationEnabled = account.smsVerificationEnabled
        state.identityCheckMetadata = account.identityCheckMetadata
        state.verifiedIdentityDocuments = transformIdentityDocument(toVerifiedDocument(account.documentVerificationResults ?? []))
        state.creditCheckConsented = account.creditCheckConsented
        state.latestFacilityStatus = account.latestFacilityStatus
      })
      .addCase(emailLookup.fulfilled, (state, action) => {
        state.email = action.meta.arg.email
        state.mobile = maskPhoneNumber(action.payload.mobile)
        state.isEmailExists = action.payload.existing
        state.hasPassword = action.payload.passwordExists
        state.smsVerificationEnabled = action.payload.smsVerificationEnabled
        state.emailLookupCountry = action.payload.countryCode
        state.trustedSession = action.payload.trustedSession ?? false
        state.cashtag = action.payload.cashtag
        state.shadowAccountExists = action.payload.shadowAccountExists
      })
      .addCase(mobileLookup.fulfilled, (state, action) => {
        if (isPublicResponse(action.payload)) {
          return
        }
        state.mobile = action.meta.arg.mobile.length > 0 ? action.meta.arg.mobile : action.payload.mobile
        state.hasPassword = action.payload.passwordExists
        state.isMobileExists = action.payload.existing
        state.cashtag = action.payload.cashtag
        state.shadowAccountExists = action.payload.shadowAccountExists
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        if (typeof action.payload.consumerLookup !== 'undefined') {
          const { cashtag, cohort, recognizedBy, email, mobile } = action.payload.consumerLookup
          state.email = email
          state.mobile = mobile
          state.isEmailExists = recognizedBy === ConsumerLookupRecognizedBy.EMAIL && cohort === ConsumerLookupCohort.existing_consumer
          state.isMobileExists = recognizedBy === ConsumerLookupRecognizedBy.MOBILE && cohort === ConsumerLookupCohort.existing_consumer
          state.hasPassword = action.payload.consumerLookup.action !== 'SET_PASSWORD'
          state.cashtag = cashtag
        }
      })
      .addCase(authReset.fulfilled, (state) => {
        state.hasPassword = true
      })
      .addCase(authResetChallenge.fulfilled, (state) => {
        state.hasPassword = true
      })
      .addCase(confirmRegistration.fulfilled, (state) => {
        state.profile = {
          ...state.profile,
          confirmed: true,
        }
      })
      .addCase(confirmVerifyAccountMobile.fulfilled, (state) => {
        state.verifyAccountMobileRequired = false
      })
      .addCase(authLogin.pending, (state) => {
        state.hasFeatureFlagsReady = false
      })
      .addCase(getIdentities.fulfilled, (state, action) => {
        state.identityCheckStatus = action.payload.identityCheckStatus
        state.identityCheckMetadata = action.payload.identityCheckMetadata
      })
      .addCase(closeCheckout, () => consumerInitialState)
      .addCase(PURGE, () => consumerInitialState)
      .addCase(getEfaCode.fulfilled, (state, action) => {
        state.mobile = maskPhoneNumber(action.payload.phoneNumber)
      })
      .addCase(identityNationalIDNumber.fulfilled, (state, action) => {
        if (state.identityCheckStatuses != null && action.payload.identityStatus === IdentityStatus.VERIFIED) {
          state.identityCheckStatuses.AFF = IdentityCheckStatus.VERIFIED
          state.identityCheckStatuses.PCL = IdentityCheckStatus.VERIFIED
        }
      })
      .addMatcher(getMarketingPreferences.matchFulfilled, (state, action) => {
        state.marketingOptIn = action.payload.status
      })
      .addMatcher(identityVerify.matchFulfilled, (state, action) => {
        state.identityCheckStatus = action.payload.identityCheckStatus
      })
      .addMatcher(checkIdentities.matchFulfilled, (state, action) => {
        state.identityCheckStatus = action.payload.identityCheckStatus
      })
      .addMatcher(startCheckout.matchRejected, (state, action) => {
        if (action.meta.rejectedWithValue && typeof action?.payload !== 'undefined' && isPortalApiError(action.payload.data)) {
          const { errorCode, errorInfo } = action.payload.data
          const isAuRegError = AU_REG_ERROR_CODES.includes(errorCode)
          if (isAuRegError) {
            state.consumerFacilityCohort = {
              // this always assumes that start is called after GET portal/consumers/account
              cohort: getFacilityCohortForNewUsers(state.latestFacilityStatus),
              getStartedContext: 'NEW_USER',
              eligibility: errorInfo?.facility_application_introduction_eligibility ?? 'not_applicable',
            }
          }
        }
      })
      .addMatcher(startCheckout.matchFulfilled, (state, action) => {
        // get new attribute from start checkout that shows if user is eligible () for Apply for Pay Monthly page
        const { facilityApplicationIntroductionEligibility: cccEligibility } = action.payload
        if (typeof cccEligibility !== 'undefined' && cccEligibility !== 'not_applicable') {
          state.consumerFacilityCohort = {
            // this always assumes that start is called after GET portal/consumers/account
            cohort: getFacilityCohortForExistingUsers(state.latestFacilityStatus),
            getStartedContext: 'LCCC_EXISTING',
            eligibility: cccEligibility,
          }
        }
      })
  },
})

export const {
  updateConsumerAddress,
  updateConsumerMobile,
  setConsumerError,
  setPreferredPaymentMethod,
  setSessionExpired,
  setFeatureFlagState,
  setIsUserVerifyAuthenticatorAvailable,
  setVerifyAccountMobileRequired,
  setLoginIdentityError,
  setCashEligibilitySignal,
  setCashCreationDate,
  setCashLoginType,
  setShadowAccountExists,
} = slice.actions
export const clearConsumer = slice.actions.clearConsumer as ActionCreatorWithoutPayload
export const clearSessionExpired = slice.actions.clearSessionExpired as ActionCreatorWithoutPayload

const { reducer } = slice
export default reducer
